import { Box, Button, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';
import {
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { DateTime } from 'luxon';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';
import { useRecodeQrCodesOutlet } from './CloudConnectorRecodeQrCodes.tsx';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import {
    maxFieldErrorMessage,
    minFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { useEasyForm } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { GenericFormSubmitError } from '../parkingabo-users/ParkingaboUsersUtils.tsx';
import {
    cloudConnectorRecodeConditionAllowsQr,
    CloudConnectorRecodeConditionType,
} from 'dg-web-shared/model/RecodeCondition.ts';

export const enum RecodeQrCodeCreateFields {
    recodeConditionId = 'recodeConditionId',
    selectedAmount = 'selectedAmount',
    selectedTimeMin = 'selectedTimeMin',
    name = 'name',
    numberOfQrCodes = 'numberOfQrCodes',
    validTo = 'validTo',
}

interface RecodeQrCodeCreateFormFieldValues {
    [RecodeQrCodeCreateFields.recodeConditionId]: string | null;
    [RecodeQrCodeCreateFields.selectedAmount]: number | null;
    [RecodeQrCodeCreateFields.selectedTimeMin]: number | null;
    [RecodeQrCodeCreateFields.name]: string;
    [RecodeQrCodeCreateFields.numberOfQrCodes]: number | null;
    [RecodeQrCodeCreateFields.validTo]: DateTime;
}

export function useCloudConnectorRecodeConditions() {
    return useServerFetch<RecodeCondition[], object>(
        () => ({
            url: `/ui-api/operator-account/cloud-connector-recode-conditions`,
        }),
        {},
    );
}

export interface RecodeCondition {
    recodeConditionId: string;
    cloudConnectorRecodeConditionType: CloudConnectorRecodeConditionType;
    description: Message;
    deactivated: boolean;
}

export function CloudConnectorRecodeQrCodesAdd() {
    const navigate = useNavigate();
    const { refetchList } = useRecodeQrCodesOutlet();
    const now = DateTime.now();
    const { language } = useLanguage();

    const [createQrCodesState, createQrCodes] = useServerWrite<
        RecodeQrCodeCreateFormFieldValues,
        null,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/cloud-connector/recode/qr-codes`,
    }));
    useServerSuccessEffect(createQrCodesState, () => {
        refetchList();
        navigate('..');
    });

    const creationPending = createQrCodesState.status === RequestStatus.PENDING;

    const {
        formInfo: { control, handleSubmit, setValue, watch },
        genericSubmitError,
    } = useEasyForm(undefined, createQrCodesState, language, {
        defaultValues: {
            [RecodeQrCodeCreateFields.recodeConditionId]: '',
            [RecodeQrCodeCreateFields.selectedAmount]: null,
            [RecodeQrCodeCreateFields.selectedTimeMin]: null,
            [RecodeQrCodeCreateFields.name]: '',
            [RecodeQrCodeCreateFields.numberOfQrCodes]: null,
            [RecodeQrCodeCreateFields.validTo]: now.plus({ year: 1 }),
        },
        mode: 'onBlur',
    });

    const [recodeConditionsState] = useCloudConnectorRecodeConditions();
    useServerSuccessEffect(recodeConditionsState, data => {
        const conditionsWithQrCodeCreateAllowed = data.filter(
            condition =>
                !condition.deactivated &&
                cloudConnectorRecodeConditionAllowsQr(
                    condition.cloudConnectorRecodeConditionType,
                ),
        );
        if (conditionsWithQrCodeCreateAllowed.length === 1) {
            setValue(
                RecodeQrCodeCreateFields.recodeConditionId,
                conditionsWithQrCodeCreateAllowed[0].recodeConditionId,
                {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                },
            );
        }
    });

    const minNumberOfQrCodes = 1;
    const maxNumberOfQrCodes = 1000;
    const minimumMessage = useTranslation(
        minFieldErrorMessage(minNumberOfQrCodes),
    );
    const maximumMessage = useTranslation(
        maxFieldErrorMessage(maxNumberOfQrCodes),
    );
    const maxLengthDescription = 30;
    const descriptionTooLong = useTranslation(
        tooManyCharsMessage(maxLengthDescription),
    );
    const recodeConditionId = watch(RecodeQrCodeCreateFields.recodeConditionId);
    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={'..'}
            render={() => (
                <OperatorAsyncLoadedSection
                    requestState={recodeConditionsState}
                    render={recodeConditions => {
                        const allowedRecodeConditions = recodeConditions.filter(
                            condition =>
                                !condition.deactivated &&
                                cloudConnectorRecodeConditionAllowsQr(
                                    condition.cloudConnectorRecodeConditionType,
                                ),
                        );
                        const selectedRecodeCondition =
                            allowedRecodeConditions.find(
                                condition =>
                                    condition.recodeConditionId ===
                                    recodeConditionId,
                            );
                        return (
                            <ParkingPortalLayoutWithHeader
                                title={
                                    <Localized
                                        de="Stapel generieren"
                                        fr="Générer set"
                                        it="Genera set"
                                        en="Generate stack"
                                    />
                                }
                                backTo={'..'}
                            >
                                <Box
                                    component="form"
                                    onSubmit={handleSubmit(
                                        (
                                            data: RecodeQrCodeCreateFormFieldValues,
                                        ) => {
                                            createQrCodes({ ...data });
                                        },
                                    )}
                                    sx={{
                                        padding: '25px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <GenericFormSubmitError
                                        submitState={createQrCodesState}
                                        error={genericSubmitError}
                                    />
                                    <ReactHookFormTextField
                                        label={
                                            <Localized
                                                de="Rabattbedingung"
                                                fr="Condition de rabais"
                                                it="Condizione ribasso"
                                                en="Discount condition"
                                            />
                                        }
                                        name={
                                            RecodeQrCodeCreateFields.recodeConditionId
                                        }
                                        disabled={
                                            allowedRecodeConditions.length === 1
                                        }
                                        control={control}
                                        select
                                        rules={{ required: true }}
                                        sx={{ paddingBottom: '25px' }}
                                    >
                                        {allowedRecodeConditions.map(
                                            recodeCondition => (
                                                <MenuItem
                                                    key={
                                                        recodeCondition.recodeConditionId
                                                    }
                                                    value={
                                                        recodeCondition.recodeConditionId
                                                    }
                                                >
                                                    <Localized
                                                        {...recodeCondition.description}
                                                    />
                                                </MenuItem>
                                            ),
                                        )}
                                    </ReactHookFormTextField>

                                    <SelectableValueFormField
                                        control={control}
                                        selectableValueType={
                                            selectedRecodeCondition?.cloudConnectorRecodeConditionType ??
                                            null
                                        }
                                        selectedAmountFieldName={
                                            RecodeQrCodeCreateFields.selectedAmount
                                        }
                                        selectedTimeMinFieldName={
                                            RecodeQrCodeCreateFields.selectedTimeMin
                                        }
                                    />

                                    <ReactHookFormTextField
                                        label={
                                            <Localized
                                                de="Bezeichnung"
                                                fr="Désignation"
                                                it="Descrizione"
                                                en="Description"
                                            />
                                        }
                                        name={RecodeQrCodeCreateFields.name}
                                        control={control}
                                        sx={{ paddingBottom: '25px' }}
                                        rules={{
                                            required: true,
                                            maxLength: {
                                                value: maxLengthDescription,
                                                message: descriptionTooLong,
                                            },
                                        }}
                                    />

                                    <ReactHookFormTextField
                                        label={
                                            <Localized
                                                de="Anzahl"
                                                fr="Quantité"
                                                it="Quantità"
                                                en="Quantity"
                                            />
                                        }
                                        type="number"
                                        name={
                                            RecodeQrCodeCreateFields.numberOfQrCodes
                                        }
                                        control={control}
                                        rules={{
                                            min: {
                                                value: minNumberOfQrCodes,
                                                message: minimumMessage,
                                            },
                                            max: {
                                                value: maxNumberOfQrCodes,
                                                message: maximumMessage,
                                            },
                                            required: true,
                                        }}
                                        sx={{
                                            paddingBottom: '25px',
                                            width: '150px',
                                        }}
                                    />

                                    <Box sx={{ paddingBottom: '25px' }}>
                                        <Controller
                                            name={
                                                RecodeQrCodeCreateFields.validTo
                                            }
                                            control={control}
                                            render={({ field }) => (
                                                <DesktopDatePicker
                                                    format="dd.MM.yyyy"
                                                    label={
                                                        <Localized
                                                            de="Gültig bis"
                                                            fr="Valide jusqu'à"
                                                            it="Valido fino a"
                                                            en="Valid Until"
                                                        />
                                                    }
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    minDate={now}
                                                    maxDate={now.plus({
                                                        years: 3,
                                                    })}
                                                />
                                            )}
                                            rules={{ required: true }}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <Button
                                            onClick={() => navigate('..')}
                                            variant="outlined"
                                            disabled={creationPending}
                                        >
                                            <Localized
                                                de="Abbrechen"
                                                fr="Annuler"
                                                it="Annulla"
                                                en="Cancel"
                                            />
                                        </Button>
                                        <SubmitButton
                                            sx={{ marginLeft: '5px' }}
                                            loading={creationPending}
                                            label={
                                                <Localized
                                                    de="Generieren"
                                                    fr="Générer"
                                                    it="Genera"
                                                    en="Generate"
                                                />
                                            }
                                        />
                                    </Box>
                                </Box>
                            </ParkingPortalLayoutWithHeader>
                        );
                    }}
                />
            )}
        />
    );
}

function SelectableValueFormField<TFieldValues extends FieldValues>({
    control,
    selectableValueType,
    selectedAmountFieldName,
    selectedTimeMinFieldName,
}: {
    control: Control<TFieldValues>;
    selectableValueType: CloudConnectorRecodeConditionType | null;
    selectedAmountFieldName: Path<TFieldValues>;
    selectedTimeMinFieldName: Path<TFieldValues>;
}) {
    const minAmount = 0.1;
    const maxAmount = 20;
    const minAmountMessage = useTranslation(minFieldErrorMessage(minAmount));
    const maxAmountMessage = useTranslation(maxFieldErrorMessage(maxAmount));

    const minTime = 15;
    const maxTime = 720;
    const minTimeMessage = useTranslation(minFieldErrorMessage(minTime));
    const maxTimeMessage = useTranslation(maxFieldErrorMessage(maxTime));

    switch (selectableValueType) {
        case CloudConnectorRecodeConditionType.QR_SELECTABLE_AMOUNT:
            return (
                <ReactHookFormTextField
                    label={
                        <Localized
                            de="Rabattbetrag (CHF - pro Karte)"
                            fr="Montant du rabais (CHF - par carte)"
                            it="Importo ribasso (CHF - per carta)"
                            en="Discount amount (CHF - per card)"
                        />
                    }
                    name={selectedAmountFieldName}
                    control={control}
                    sx={{ paddingBottom: '25px' }}
                    type="number"
                    rules={{
                        min: {
                            value: minAmount,
                            message: minAmountMessage,
                        },
                        max: {
                            value: maxAmount,
                            message: maxAmountMessage,
                        },
                        required: true,
                    }}
                />
            );
        case CloudConnectorRecodeConditionType.QR_SELECTABLE_TIME:
            return (
                <ReactHookFormTextField
                    label={
                        <Localized
                            de="Gratiszeit (Minuten - pro Karte)"
                            fr="Temps gratuit (minutes - par carte)"
                            it="Tempo gratuito (minuti - per carta)"
                            en="Free time (minutes - per card)"
                        />
                    }
                    name={selectedTimeMinFieldName}
                    control={control}
                    type="number"
                    sx={{ paddingBottom: '25px' }}
                    rules={{
                        min: {
                            value: minTime,
                            message: minTimeMessage,
                        },
                        max: {
                            value: maxTime,
                            message: maxTimeMessage,
                        },
                        required: true,
                    }}
                />
            );
        case CloudConnectorRecodeConditionType.DEVICE_ONLY:
        case CloudConnectorRecodeConditionType.QR_AND_DEVICE:
        case null:
            return null;
    }
}
